import { useEffect, useRef, useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { extractApiError } from '../utils/errorUtils';

/**
 * Waitlist Event Types (Issue #497)
 */
export type WaitlistEventType =
  | 'position_changed'
  | 'slot_offered'
  | 'offer_expiring'
  | 'offer_expired'
  | 'removed_from_waitlist'
  | 'heartbeat';

/**
 * Waitlist Event Data Structure
 */
export interface WaitlistEvent {
  event_id: number;
  event_type: WaitlistEventType;
  fair_id: number;
  waitlist_id?: number;
  timestamp: string;

  // Event-specific fields
  old_position?: number;
  new_position?: number;
  position_improved?: boolean;
  offer_expires_at?: string;
  reason?: string;
  status?: string;
}

/**
 * Connection Status
 */
export type ConnectionStatus = 'connected' | 'connecting' | 'disconnected' | 'error';

/**
 * Event Handlers
 */
export interface WaitlistEventHandlers {
  onPositionChanged?: (event: WaitlistEvent) => void;
  onSlotOffered?: (event: WaitlistEvent) => void;
  onOfferExpiring?: (event: WaitlistEvent) => void;
  onOfferExpired?: (event: WaitlistEvent) => void;
  onRemovedFromWaitlist?: (event: WaitlistEvent) => void;
  onError?: (error: Error) => void;
}

/**
 * useWaitlistEvents Hook
 *
 * Establishes SSE connection to receive real-time waitlist updates (Issue #497)
 *
 * Features:
 * - Auto-reconnection with exponential backoff
 * - Connection status tracking
 * - Event-specific handlers
 * - Automatic cleanup on unmount
 * - JWT authentication
 *
 * @param enabled - Enable/disable SSE connection
 * @param handlers - Event-specific callback handlers
 * @returns Connection status and manual reconnect function
 */
export function useWaitlistEvents(
  enabled: boolean = true,
  handlers: WaitlistEventHandlers = {}
) {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 1000; // 1 second

  /**
   * Calculate exponential backoff delay
   */
  const getReconnectDelay = useCallback(() => {
    const attempts = reconnectAttemptsRef.current;
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 32s (max 32s)
    const delay = Math.min(baseReconnectDelay * Math.pow(2, attempts), 32000);
    return delay;
  }, []);

  /**
   * Connect to SSE endpoint
   */
  const connect = useCallback(() => {
    // Don't reconnect if disabled or already connected
    if (!enabled || eventSourceRef.current) {
      return;
    }

    console.log('[SSE] Connecting to waitlist events stream...');
    setConnectionStatus('connecting');

    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('[SSE] No auth token found');
        setConnectionStatus('error');
        return;
      }

      // Create EventSource with auth header (via URL parameter for SSE compatibility)
      // Note: EventSource doesn't support custom headers, so we pass token as query param
      const url = `/api/user/waitlist/events?token=${encodeURIComponent(token)}`;
      const eventSource = new EventSource(url);

      eventSourceRef.current = eventSource;

      // Connection opened
      eventSource.onopen = () => {
        console.log('[SSE] Connected to waitlist events stream');
        setConnectionStatus('connected');
        reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
      };

      // Handle position_changed events
      eventSource.addEventListener('position_changed', (e: MessageEvent) => {
        try {
          const event: WaitlistEvent = JSON.parse(e.data);
          console.log('[SSE] Position changed:', event);

          // Show toast notification
          if (event.position_improved) {
            toast.success(
              `Your waitlist position improved to #${event.new_position}!`,
              { duration: 5000 }
            );
          }

          // Call handler
          handlers.onPositionChanged?.(event);
        } catch (error: unknown) {
          const apiError = extractApiError(error);
          console.error('[SSE] Failed to parse position_changed event:', apiError);
        }
      });

      // Handle slot_offered events
      eventSource.addEventListener('slot_offered', (e: MessageEvent) => {
        try {
          const event: WaitlistEvent = JSON.parse(e.data);
          console.log('[SSE] Slot offered:', event);

          // Show urgent toast notification
          toast.success(
            '🎉 You have a new slot offer! Check your waitlist.',
            { duration: 10000 }
          );

          // Call handler
          handlers.onSlotOffered?.(event);
        } catch (error: unknown) {
          const apiError = extractApiError(error);
          console.error('[SSE] Failed to parse slot_offered event:', apiError);
        }
      });

      // Handle offer_expiring events
      eventSource.addEventListener('offer_expiring', (e: MessageEvent) => {
        try {
          const event: WaitlistEvent = JSON.parse(e.data);
          console.log('[SSE] Offer expiring:', event);

          // Show warning toast
          toast.error(
            '⚠️ Your slot offer is expiring soon! Act now.',
            { duration: 8000 }
          );

          // Call handler
          handlers.onOfferExpiring?.(event);
        } catch (error: unknown) {
          const apiError = extractApiError(error);
          console.error('[SSE] Failed to parse offer_expiring event:', apiError);
        }
      });

      // Handle offer_expired events
      eventSource.addEventListener('offer_expired', (e: MessageEvent) => {
        try {
          const event: WaitlistEvent = JSON.parse(e.data);
          console.log('[SSE] Offer expired:', event);

          // Show error toast
          toast.error(
            'Your slot offer has expired.',
            { duration: 5000 }
          );

          // Call handler
          handlers.onOfferExpired?.(event);
        } catch (error: unknown) {
          const apiError = extractApiError(error);
          console.error('[SSE] Failed to parse offer_expired event:', apiError);
        }
      });

      // Handle removed_from_waitlist events
      eventSource.addEventListener('removed_from_waitlist', (e: MessageEvent) => {
        try {
          const event: WaitlistEvent = JSON.parse(e.data);
          console.log('[SSE] Removed from waitlist:', event);

          // Show info toast
          toast('You have been removed from the waitlist.', { duration: 5000 });

          // Call handler
          handlers.onRemovedFromWaitlist?.(event);
        } catch (error: unknown) {
          const apiError = extractApiError(error);
          console.error('[SSE] Failed to parse removed_from_waitlist event:', apiError);
        }
      });

      // Handle heartbeat (keepalive)
      eventSource.addEventListener('heartbeat', () => {
        console.debug('[SSE] Heartbeat received');
        // Just log, no action needed
      });

      // Handle errors
      eventSource.onerror = (error) => {
        console.error('[SSE] Connection error:', error);
        setConnectionStatus('error');

        // Close current connection
        eventSource.close();
        eventSourceRef.current = null;

        // Attempt to reconnect with exponential backoff
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = getReconnectDelay();
          console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})...`);

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current += 1;
            connect();
          }, delay);
        } else {
          console.error('[SSE] Max reconnection attempts reached');
          toast.error('Lost connection to live updates. Please refresh the page.');
          handlers.onError?.(new Error('Max reconnection attempts reached'));
        }
      };

    } catch (error: unknown) {
      const apiError = extractApiError(error);
      console.error('[SSE] Failed to establish connection:', apiError);
      setConnectionStatus('error');
      handlers.onError?.(new Error(apiError.message));
    }
  }, [enabled, handlers, getReconnectDelay]);

  /**
   * Disconnect from SSE endpoint
   */
  const disconnect = useCallback(() => {
    console.log('[SSE] Disconnecting from waitlist events stream');

    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close EventSource
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setConnectionStatus('disconnected');
  }, []);

  /**
   * Manual reconnect (resets attempt counter)
   */
  const reconnect = useCallback(() => {
    console.log('[SSE] Manual reconnect requested');
    reconnectAttemptsRef.current = 0;
    disconnect();
    setTimeout(() => connect(), 100);
  }, [connect, disconnect]);

  /**
   * Auto-connect on mount or when enabled changes
   */
  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]); // connect and disconnect are memoized with useCallback to prevent infinite loops

  return {
    connectionStatus,
    reconnect
  };
}
